export enum PerformanceTier {
  Low = 0,
  Medium = 1,
  High = 2,
  Ultra = 3,
  SuperUltra = 4,
}

export function getPerformanceTierName(tier: PerformanceTier): string {
  switch (tier) {
    case PerformanceTier.Low:
      return "Low";
    case PerformanceTier.Medium:
      return "Medium";
    case PerformanceTier.High:
      return "High";
    case PerformanceTier.Ultra:
      return "Ultra";
    case PerformanceTier.SuperUltra:
      return "SuperUltra";
    default:
      return "Unknown";
  }
}

export interface BenchmarkResult {
  tier: PerformanceTier;
  score: number; // The level reached (how many times SUBDIV was doubled)
  maxSubdiv: number; // The maximum SUBDIV value achieved
  averageFrameTime: number; // Average frame time in milliseconds
  timerType: "gpu" | "cpu"; // Whether GPU timer was available
}

export type Thresholds = {
  SUPERULTRA: number;
  ULTRA: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
};

export interface BenchmarkConfig {
  maxFrameTime: number; // Target frame time in ms (default: 16)
  warmupRuns: number; // Number of warmup runs before measurement (default: 3)
  startSubdiv: number; // Starting SUBDIV value (default: 32)
  maxSubdiv: number; // Maximum SUBDIV value to test (default: 4096)
  timeout: number; // Total benchmark timeout in ms (default: 10000)
  canvasSize: number; // Size of the benchmark canvas (default: 512)
  framesPerTest: number; // Number of frames to render per test for timer accuracy (default: 10)
  debug?: boolean; // Enable debug logging (default: false)
  thresholds?: Thresholds;
}

export const TIER_THRESHOLDS = {
  SUPERULTRA: 1024,
  ULTRA: 512,
  HIGH: 164,
  MEDIUM: 48,
  LOW: 0,
};

export const DEFAULT_CONFIG: BenchmarkConfig = {
  maxFrameTime: 1000 / 50,
  warmupRuns: 3,
  startSubdiv: 8, // First value in SUBDIV_SEQUENCE
  maxSubdiv: 4096, // Last value in SUBDIV_SEQUENCE
  timeout: 12000,
  canvasSize: 512,
  framesPerTest: 25, // Increased for better precision with fence sync
  thresholds: TIER_THRESHOLDS,
};

// Predefined SUBDIV progression sequence
// Provides ~1.5x average multiplier for finer granularity at higher levels
//export const SUBDIV_SEQUENCE = [8, 32, 64, 96, 128, 256, 384, 512, 768, 1024, 1536, 2048] as const;
export const SUBDIV_SEQUENCE = [
  8,
  TIER_THRESHOLDS.MEDIUM,
  TIER_THRESHOLDS.HIGH,
  TIER_THRESHOLDS.ULTRA,
  TIER_THRESHOLDS.SUPERULTRA,
] as const;

export function calculatePerformanceTier(maxSubdiv: number, thresholds: Thresholds = TIER_THRESHOLDS): PerformanceTier {
  if (maxSubdiv >= thresholds.SUPERULTRA) {
    return PerformanceTier.SuperUltra;
  } else if (maxSubdiv >= thresholds.ULTRA) {
    return PerformanceTier.Ultra;
  } else if (maxSubdiv >= thresholds.HIGH) {
    return PerformanceTier.High;
  } else if (maxSubdiv >= thresholds.MEDIUM) {
    return PerformanceTier.Medium;
  }
  return PerformanceTier.Low;
}

export function getScoreFromSubdiv(
  subdiv: number,
  startSubdiv: number,
  thresholds: Thresholds = TIER_THRESHOLDS
): number {
  // Create dynamic sequence based on thresholds
  const subdivSequence = [8, thresholds.MEDIUM, thresholds.HIGH, thresholds.ULTRA, thresholds.SUPERULTRA];

  // Find position in sequence
  const index = subdivSequence.findIndex((val) => val === subdiv);
  const startIndex = subdivSequence.findIndex((val) => val >= startSubdiv);

  if (index === -1 || startIndex === -1) {
    // Fallback to complexity-based score if not in sequence
    return Math.log2(subdiv / startSubdiv);
  }

  // Return steps progressed from start
  return index - startIndex;
}
